import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Modal, Button, Tab, Tabs, Form } from "react-bootstrap";
import {
  imageCustomOrderUrl,
  getUserData,
  getToken,
  encryptData,
  node_imageBaseUrl,
} from "./const";
import toast, { Toaster } from "react-hot-toast";
import { Icon } from "@iconify/react";
import statusColors from "../utils/statusColors";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import {
  sanitizeMobileInput,
  isValidMobileNumber,
} from "../utils/mobileInput";
import { useMobileExistsCheck } from "../utils/useMobileExistsCheck";
import MobileExistsFeedback from "../components/MobileExistsFeedback";
import {
  listProducts,
  isErpApiSuccess,
  postCustomerForm,
  postReadyProducts,
  postSaleNotCompleted,
  postOrderQueue,
  postOrderQueueMultipart,
  postSelfCustomerForm,
} from "../erpClient";
import { normalizeProductStockListResponse } from "../utils/productStockErpAdapter";
import {
  searchProductGroupSelectOptions,
  mergeProductGroupOptions,
  enrichProductGroupSelectOption,
} from "../utils/productGroupOptions";
import {
  buildMobileSelectOptions,
  buildNameSelectOptions,
  customerSelectMenuProps,
  fetchCustomerUserSearch,
} from "../utils/customerUserSelectOptions";

import { createErpSocket } from "../utils/socketIo";

const ASSIGNED_STORAGE_KEY = "mg-assigned-customer-ids";

// Light row backgrounds (same low-opacity approach as Invoice.jsx)
const customerAssignRowStyles = `
  .status-unassigned-row,
  .status-unassigned-row td {
    background-color: rgba(255, 193, 7, 0.02) !important; /* amber */
  }
  .status-assigned-row,
  .status-assigned-row td {
    background-color: rgba(25, 135, 84, 0.02) !important; /* green */
  }
  .status-deactive-row,
  .status-deactive-row td {
    background-color: rgba(108, 117, 125, 0.03) !important; /* gray */
  }
`;

if (typeof document !== "undefined") {
  const existingStyle = document.getElementById("customer-assign-row-styles");
  if (existingStyle) {
    existingStyle.remove();
  }
  const styleSheet = document.createElement("style");
  styleSheet.id = "customer-assign-row-styles";
  styleSheet.type = "text/css";
  styleSheet.innerText = customerAssignRowStyles;
  document.head.appendChild(styleSheet);
}

const getCustomerUserId = (customer) =>
  customer?.customer_user_id ??
  customer?.user_id ??
  customer?.customer_id ??
  null;

const getCustomerDisplayName = (customer) => {
  if (customer?.user_name) return customer.user_name;
  if (customer?.name) return customer.name;
  return "N/A";
};

const getCustomerDisplayMobile = (customer) => {
  if (customer?.user_mobile) return customer.user_mobile;
  if (customer?.mobile) return customer.mobile;
  return "N/A";
};

const getStatusRowClass = (status) => {
  const value = String(status);
  if (value === "1") return "status-unassigned-row";
  if (value === "2") return "status-assigned-row";
  if (value === "0") return "status-deactive-row";
  return "";
};

const getStoredAssignedCustomers = () => {
  if (typeof window === "undefined") return {};
  try {
    const stored = window.localStorage.getItem(ASSIGNED_STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.error("Failed to parse stored assigned customers:", error);
    return {};
  }
};

const getProductImageUrl = (imagePath) => {
  if (!imagePath) return "https://i.ibb.co/rKZrvh64/jewellery-showcase.png";

  if (imagePath.startsWith("http")) {
    return imagePath;
  } else if (
    imagePath.startsWith("uploads/") ||
    imagePath.startsWith("upload/")
  ) {
    return `${node_imageBaseUrl}/${imagePath}`;
  } else {
    return `${node_imageBaseUrl}/upload/product/${imagePath}`;
  }
};

const CustomerAssignTable = ({ masterGoldRate, masterSilverRate }) => {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();
  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsTab, setDetailsTab] = useState("sellCompleted");
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [assignedCustomers, setAssignedCustomersState] = useState(() =>
    getStoredAssignedCustomers(),
  );
  const [isAssigned, setIsAssigned] = useState(
    () => Object.keys(getStoredAssignedCustomers()).length > 0,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showFields, setShowFields] = useState(false);
  // Add state for ready products price information
  const [readyProductsPriceInfo, setReadyProductsPriceInfo] = useState({
    priceType: "",
    priceTypeValue: "",
  });
  const [orderList, setOrderList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const customersRef = useRef([]);
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState({});
  const [productGroupOptions, setProductGroupOptions] = useState([]);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const [lastSearchedProductGroupPrefix, setLastSearchedProductGroupPrefix] =
    useState("");
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orderDetails, setOrderDetails] = useState({
    productGroupId: "",
    size: "",
    weightMin: "",
    weightMax: "",
    priceMin: "",
    priceMax: "",
    deliveryDate: "",
    addOns: "",
    designPhoto: null,
    metal: "",
    purity: "",
    priceType: "",
    priceTypeValue: "",
  });
  // Add state for expected price
  const [expectedPrice, setExpectedPrice] = useState({ min: "", max: "" });
  const [isManualExpectedPrice, setIsManualExpectedPrice] = useState(false);
  const [saleNotCompletedForm, setSaleNotCompletedForm] = useState({
    reason: "",
    requirements: "",
    response_date: "",
    feedback: "",
  });
  const [repairingOrderForm, setRepairingOrderForm] = useState({
    productGroupId: "",
    size: "",
    issue_description: "",
    designPhoto: null,
    current_weight: "",
    expected_weight: "",
    weightMin: "",
    weightMax: "",
    priceType: "",
    priceTypeValue: "",
    exp_delivery: "",
    metal: "",
    purity: "",
  });
  const [repairingExpectedPrice, setRepairingExpectedPrice] = useState("");
  const [isManualPriceEntry, setIsManualPriceEntry] = useState(false);
  const [repairingOrderList, setRepairingOrderList] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // State for add customer form
  const [fullName, setFullName] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [state, setState] = useState("Gujarat");
  const [purposeOfVisit, setPurposeOfVisit] = useState("");
  const [noOfVisitors, setNoOfVisitors] = useState(1);
  const [functionPurpose, setFunctionPurpose] = useState("");
  const [functionDate, setFunctionDate] = useState("");
  const [scheme, setScheme] = useState("No");
  const [instagramFollow, setInstagramFollow] = useState("No");
  const [reference, setReference] = useState("");
  const [selectedMobile, setSelectedMobile] = useState(null);
  const [selectedName, setSelectedName] = useState(null);
  const [mobileInputValue, setMobileInputValue] = useState("");
  const [nameInputValue, setNameInputValue] = useState("");
  const isExistingCustomerSelected =
    (selectedMobile && !selectedMobile.__isNew__) ||
    (selectedName && !selectedName.__isNew__ && selectedName.mobile);

  const { states, selectedState, setSelectedState } = useIndiaStateCity("GJ");

  const stateOptions = states.map((s) => ({
    label: s.name,
    value: s.iso2,
  }));

  useEffect(() => {
    const gujarat = states.find((s) => s.name === "Gujarat");
    if (gujarat) {
      setState(gujarat.name);
      setSelectedState(gujarat.iso2);
    }
  }, [states, setSelectedState]);

  const assignMobileExistsCheck = useMobileExistsCheck(
    mobileInputValue || mobileNo,
    {
      enabled:
        showAddCustomerModal &&
        !isExistingCustomerSelected &&
        (!selectedMobile || selectedMobile.__isNew__),
    },
  );
  const [mobileOptions, setMobileOptions] = useState([]);
  const [nameOptions, setNameOptions] = useState([]);
  const [loadingMobileSearch, setLoadingMobileSearch] = useState(false);
  const [loadingNameSearch, setLoadingNameSearch] = useState(false);
  const mobileSearchTimerRef = useRef(null);
  const nameSearchTimerRef = useRef(null);
  const productSearchTimerRef = useRef(null);
  const productGroupSearchTimerRef = useRef(null);
  const customersFetchStartedRef = useRef(false);
  const customersFetchDoneRef = useRef(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [socket, setSocket] = useState(null);
  const userData = getUserData();

  useEffect(() => {
    customersRef.current = customers;
  }, [customers]);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 44,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
      borderColor: "#e0e6eb",
      borderWidth: "var(--bs-border-width)",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#e0e6eb",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0.25rem 0.65rem",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#6c757d",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#212529",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      borderRadius: buttonTableStyle === "square" ? 0 : 8,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 220,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
  };

  const updateAssignedCustomers = useCallback((updater) => {
    setAssignedCustomersState((prev = {}) => {
      const previous = prev && typeof prev === "object" ? prev : {};
      const nextValue =
        typeof updater === "function" ? updater(previous) : updater || {};
      const normalizedNext =
        nextValue && typeof nextValue === "object" ? nextValue : {};

      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          ASSIGNED_STORAGE_KEY,
          JSON.stringify(normalizedNext),
        );
      }
      setIsAssigned(Object.keys(normalizedNext).length > 0);
      return normalizedNext;
    });
  }, []);

  const getStatusLabel = (status) => {
    const value = String(status);
    if (value === "2") return "Assigned";
    if (value === "1") return "Unassigned";
    return "Completed";
  };

  const filteredCustomers = useMemo(() => {
    const query = customerSearch.trim().toLowerCase();
    return customers.filter((customer) => {
      const matchesStatus =
        !statusFilter || getStatusLabel(customer.status) === statusFilter;
      const displayName = getCustomerDisplayName(customer).toLowerCase();
      const displayMobile = getCustomerDisplayMobile(customer).toLowerCase();
      const matchesSearch =
        query === "" ||
        displayName.includes(query) ||
        displayMobile.includes(query) ||
        (customer.purpose_of_visit &&
          customer.purpose_of_visit.toLowerCase().includes(query)) ||
        (customer.token_number &&
          String(customer.token_number).toLowerCase().includes(query)) ||
        (customer.id && String(customer.id).toLowerCase().includes(query));
      return matchesStatus && matchesSearch;
    });
  }, [customers, statusFilter, customerSearch]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const displayedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredCustomers, currentPage, itemsPerPage]);

  // Reset page if it becomes invalid when filtered results change
  useEffect(() => {
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    if (totalPages === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredCustomers.length, itemsPerPage, currentPage]);

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postCustomerForm({ type: "show" });

      if (isErpApiSuccess(data) || data?.message === "radha") {
        const customersData = data.data || [];
        const sortedCustomers = [...customersData].sort(
          (a, b) => parseInt(b.id) - parseInt(a.id),
        );
        setCustomers(sortedCustomers);

        const initialAssignedCustomers = {};
        const storedAssigned = getStoredAssignedCustomers();
        sortedCustomers.forEach((customer) => {
          const tokenNumber = customer.token_number || customer.id;
          const hasSelfInToken = String(tokenNumber)
            .toUpperCase()
            .endsWith("SELF");
          const isStatusAssigned =
            customer.status === 2 || customer.status === "2";
          const storedMatch = storedAssigned[String(customer.id)];
          if (isStatusAssigned && (hasSelfInToken || storedMatch)) {
            initialAssignedCustomers[customer.id] = true;
          }
        });
        updateAssignedCustomers(initialAssignedCustomers);
      } else {
        console.error("Unexpected API response format:", data);
        setCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
    } finally {
      setLoading(false);
      customersFetchDoneRef.current = true;
      customersFetchStartedRef.current = false;
    }
  }, [updateAssignedCustomers]);

  // Fetch customer_form once on mount
  useEffect(() => {
    if (customersFetchStartedRef.current || customersFetchDoneRef.current) {
      return;
    }
    customersFetchStartedRef.current = true;
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    if (!showDetailsModal) return undefined;
    clearTimeout(productSearchTimerRef.current);
    const q = searchTerm.trim();
    if (!q) {
      setProducts([]);
      setProductsLoading(false);
      return undefined;
    }
    setProductsLoading(true);
    productSearchTimerRef.current = setTimeout(async () => {
      try {
        const response = await listProducts({
          search: q,
          page: 1,
          limit: 20,
        });
        const rows = normalizeProductStockListResponse(response);
        setProducts(rows);
      } catch (error) {
        console.error("Error searching products:", error);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    }, 400);
    return () => clearTimeout(productSearchTimerRef.current);
  }, [searchTerm, showDetailsModal]);

  useEffect(() => {
    return () => {
      clearTimeout(mobileSearchTimerRef.current);
      clearTimeout(nameSearchTimerRef.current);
      clearTimeout(productSearchTimerRef.current);
      clearTimeout(productGroupSearchTimerRef.current);
    };
  }, []);

  const scheduleMobileSearch = (rawInput) => {
    clearTimeout(mobileSearchTimerRef.current);
    const sanitized = sanitizeMobileInput(rawInput);
    setMobileInputValue(sanitized);
    if (!sanitized.trim()) {
      setMobileOptions([]);
      setLoadingMobileSearch(false);
      return;
    }
    setLoadingMobileSearch(true);
    mobileSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(sanitized);
        setMobileOptions(buildMobileSelectOptions(rows, sanitized));
      } catch {
        setMobileOptions([]);
      } finally {
        setLoadingMobileSearch(false);
      }
    }, 400);
  };

  const scheduleNameSearch = (rawInput) => {
    clearTimeout(nameSearchTimerRef.current);
    setNameInputValue(rawInput);
    if (!String(rawInput ?? "").trim()) {
      setNameOptions([]);
      setLoadingNameSearch(false);
      return;
    }
    setLoadingNameSearch(true);
    nameSearchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await fetchCustomerUserSearch(rawInput);
        setNameOptions(buildNameSelectOptions(rows, rawInput));
      } catch {
        setNameOptions([]);
      } finally {
        setLoadingNameSearch(false);
      }
    }, 400);
  };

  const scheduleProductGroupSearch = (rawInput) => {
    clearTimeout(productGroupSearchTimerRef.current);
    const trimmed = String(rawInput ?? "").trim();
    if (trimmed.length < 3) {
      setProductGroupSearchLoading(false);
      setLastSearchedProductGroupPrefix("");
      // Keep currently selected option visible when clearing search text
      setProductGroupOptions((prev) =>
        prev.filter(
          (opt) =>
            String(opt.value) === String(orderDetails.productGroupId || "") ||
            String(opt.value) ===
            String(repairingOrderForm.productGroupId || ""),
        ),
      );
      return;
    }
    if (lastSearchedProductGroupPrefix === trimmed) return;

    setProductGroupSearchLoading(true);
    productGroupSearchTimerRef.current = setTimeout(async () => {
      try {
        const opts = await searchProductGroupSelectOptions(trimmed);
        setProductGroupOptions((prev) => {
          const selectedIds = [
            orderDetails.productGroupId,
            repairingOrderForm.productGroupId,
          ].filter((id) => id != null && id !== "");
          const keepSelected = prev.filter((opt) =>
            selectedIds.some((id) => String(opt.value) === String(id)),
          );
          return mergeProductGroupOptions(keepSelected, opts);
        });
        setLastSearchedProductGroupPrefix(trimmed);
      } catch (error) {
        console.error("Error searching product groups:", error);
        setProductGroupOptions([]);
      } finally {
        setProductGroupSearchLoading(false);
      }
    }, 400);
  };

  const rememberProductGroupSelection = (selectedOption) => {
    if (!selectedOption?.value) return;
    const label =
      selectedOption.label ||
      selectedOption.name ||
      `Group ${selectedOption.value}`;
    setProductGroups((prev) => ({
      ...prev,
      [selectedOption.value]: label,
      [String(selectedOption.value)]: label,
    }));
    setProductGroupOptions((prev) =>
      mergeProductGroupOptions(prev, [selectedOption]),
    );
  };

  // Listen for API URL changes and refetch customer_form
  useEffect(() => {
    const handleApiUrlChange = () => {
      setCurrentPage(1);
      customersFetchDoneRef.current = false;
      customersFetchStartedRef.current = false;
      fetchCustomers();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, [fetchCustomers]);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log(
      //   "CustomerAssignTable: Connected to socket server",
      //   newSocket.id,
      // );
    });

    newSocket.on("disconnect", (reason) => {
      // console.log(
      //   "CustomerAssignTable: Disconnected from socket server:",
      //   reason,
      // );
    });

    newSocket.on("connect_error", (error) => {
      console.error("CustomerAssignTable: Socket connection error:", error);
    });

    newSocket.on("customer_form_created", (data) => {
      if (!data?.customer_form_data) return;

      const newCustomer = data.customer_form_data;
      if (String(newCustomer.status) === "0") return;

      setCustomers((prev) => {
        // Check if customer already exists (avoid duplicates)
        const exists = prev.some(
          (customer) => String(customer.id) === String(newCustomer.id),
        );
        if (exists) {
          return prev.map((customer) =>
            String(customer.id) === String(newCustomer.id)
              ? { ...customer, ...newCustomer }
              : customer,
          );
        }
        // Add new customer at the beginning (newest first)
        return [newCustomer, ...prev].sort(
          (a, b) => parseInt(b.id) - parseInt(a.id),
        );
      });

      // Handle assignment logic for new customer
      const tokenNumber = newCustomer.token_number || newCustomer.id;
      const hasSelfInToken = String(tokenNumber).toUpperCase().endsWith("SELF");
      const isStatusAssigned =
        newCustomer.status === 2 || newCustomer.status === "2";

      if (isStatusAssigned && hasSelfInToken) {
        updateAssignedCustomers((prev) => ({
          ...prev,
          [newCustomer.id]: true,
        }));
      }

      // toast.success(
      //   `New customer created: ${newCustomer.token_number || "Unknown"}`
      // );
    });

    newSocket.on("self_customer_form_created", (data) => {
      if (!data?.token) return;
      postCustomerForm({ type: "show" })
        .then((res) => {
          if (!isErpApiSuccess(res) && res?.message !== "radha") return;
          const rows = res.data || [];
          const match = rows.find(
            (c) =>
              String(c.token_number || "").toUpperCase() ===
              String(data.token).toUpperCase() ||
              String(c.id) === String(data.id),
          );
          if (match) {
            setCustomers((prev) => {
              const exists = prev.some(
                (c) => String(c.id) === String(match.id),
              );
              if (exists) {
                return prev.map((c) =>
                  String(c.id) === String(match.id) ? { ...c, ...match } : c,
                );
              }
              return [match, ...prev].sort(
                (a, b) => parseInt(b.id) - parseInt(a.id),
              );
            });
            updateAssignedCustomers((prev) => ({ ...prev, [match.id]: true }));
          }
        })
        .catch((err) => console.warn("self_customer_form refresh failed", err));
    });

    newSocket.on("customer_form_updated", (data) => {
      if (data?.customer_form_data) {
        // Full customer form data provided
        const updatedCustomer = data.customer_form_data;
        setCustomers((prev) =>
          prev.map((customer) =>
            String(customer.id) === String(updatedCustomer.id)
              ? { ...customer, ...updatedCustomer }
              : customer,
          ),
        );

        // Update assigned customers state if status changed
        if (updatedCustomer.status === 2 || updatedCustomer.status === "2") {
          const tokenNumber =
            updatedCustomer.token_number || updatedCustomer.id;
          const hasSelfInToken = String(tokenNumber)
            .toUpperCase()
            .endsWith("SELF");
          const storedAssigned = getStoredAssignedCustomers();
          const storedMatch = storedAssigned[String(updatedCustomer.id)];

          if (hasSelfInToken || storedMatch) {
            updateAssignedCustomers((prev) => ({
              ...prev,
              [updatedCustomer.id]: true,
            }));
          }
        } else if (
          updatedCustomer.status === 0 ||
          updatedCustomer.status === "0"
        ) {
          // Remove from assigned customers when status becomes 0
          updateAssignedCustomers((prev) => {
            const updated = { ...prev };
            delete updated[updatedCustomer.id];
            delete updated[String(updatedCustomer.id)];
            return updated;
          });
        }
      } else if (data?.id && data?.status !== undefined) {
        // Status-only update
        setCustomers((prev) =>
          prev.map((customer) =>
            String(customer.id) === String(data.id)
              ? { ...customer, status: data.status }
              : customer,
          ),
        );

        // Handle assigned customers state for status-only updates
        if (data.status === 2 || data.status === "2") {
          // Find the customer to check token pattern
          const customer = customersRef.current.find(
            (c) => String(c.id) === String(data.id),
          );
          if (customer) {
            const tokenNumber = customer.token_number || customer.id;
            const hasSelfInToken = String(tokenNumber)
              .toUpperCase()
              .endsWith("SELF");
            const storedAssigned = getStoredAssignedCustomers();
            const storedMatch = storedAssigned[String(data.id)];

            if (hasSelfInToken || storedMatch) {
              updateAssignedCustomers((prev) => ({
                ...prev,
                [data.id]: true,
              }));
            }
          }
        } else if (data.status === 0 || data.status === "0") {
          updateAssignedCustomers((prev) => {
            const updated = { ...prev };
            delete updated[data.id];
            delete updated[String(data.id)];
            return updated;
          });
        }
      }
    });

    return () => {
      newSocket.off("customer_form_created");
      newSocket.off("self_customer_form_created");
      newSocket.off("customer_form_updated");
      newSocket.disconnect();
    };
  }, [updateAssignedCustomers]);

  const handleAssignClick = (customer) => {
    if (isAssigned) {
      setShowWarningModal(true);
      return;
    }
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleConfirmAssign = async () => {
    if (!selectedCustomer) return;

    try {
      const statusResponse = await postCustomerForm({
        type: "status",
        id: selectedCustomer.id,
        status: "2",
      });

      if (statusResponse) {
        const updatedCustomer = { ...selectedCustomer, status: "2" };

        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer.id === updatedCustomer.id ? updatedCustomer : customer,
          ),
        );

        updateAssignedCustomers((prev) => ({
          ...prev,
          [updatedCustomer.id]: true,
        }));
        setSelectedCustomer(updatedCustomer);
        setShowModal(false);
        setShowDetailsModal(true);
      } else {
        toast.error(
          statusResponse?.message || "Failed to update customer status",
        );
        setShowModal(false);
        setSelectedCustomer(null);
      }
    } catch (error) {
      console.error("Error updating customer status:", error);
      toast.error(
        error.response?.data?.message || "Failed to update customer status",
      );
      setShowModal(false);
      setSelectedCustomer(null);
    }
  };

  const handleAddDetailsClick = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setShowWarningModal(false);
    setShowDetailsModal(false);
    setSelectedCustomer(null);
    // Reset selected products and order list
    setSelectedProducts([]);
    setOrderList([]);
    setRepairingOrderList([]);
    // Reset search term
    setSearchTerm("");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleProductSelect = (product) => {
    // Check if this product is already selected
    if (!selectedProducts.some((p) => p.id === product.id)) {
      // First add the product to selected products
      setSelectedProducts((prevSelected) => [...prevSelected, product]);

      // Then clear the search term with a slight delay to prevent flickering
      setTimeout(() => {
        setSearchTerm("");
      }, 100);
    } else {
      // If already selected, just clear the search
      setSearchTerm("");
    }
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.filter((product) => product.id !== productId),
    );
  };

  const handleSubmit = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Please select at least one product.");
      return;
    }

    try {
      setSubmitting(true);

      // Submit each selected product
      const promises = selectedProducts.map((product) =>
        postReadyProducts({
          type: "create",
          user_id: selectedCustomer.id,
          pro_id: product.id,
          price_type: "",
          price_value: "",
        }),
      );
      // console.log("Submitting node ready products:", selectedProducts);

      const results = await Promise.all(promises);

      // Check if all API calls were successful
      const allSuccessful = results.every(
        (result) =>
          result?.status === "success" || result?.data?.status === "success",
      );

      if (allSuccessful) {
        // Update customer status to 0 (Deactive)
        const statusResponse = await postCustomerForm({
          type: "status",
          id: selectedCustomer.id,
          status: "0",
        });

        if (statusResponse) {
          // Update local state to reflect changes
          setCustomers((prevCustomers) =>
            prevCustomers.map((customer) =>
              customer.id === selectedCustomer.id
                ? { ...customer, status: "0" }
                : customer,
            ),
          );
          // Reset the assigned customer status to allow assigning a new customer
          updateAssignedCustomers((prev) => {
            const updated = { ...prev };
            delete updated[selectedCustomer.id];
            return updated;
          });
        } else {
          console.error("Failed to update customer status to deactive");
        }

        setShowDetailsModal(false);
        // Use the message from the first successful result
        const successMessage =
          results[0]?.message ||
          results[0]?.data?.message ||
          "Products submitted successfully!";
        toast.success(successMessage);

        // Clear selected products after successful submission
        setSelectedProducts([]);
      } else {
        // Find first error message to display
        const errorMessage =
          results.find(
            (result) => result.data && result.data.status === "error",
          )?.data?.message || "Some products failed to submit.";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error submitting ready products:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to submit products. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddToOrders = (e) => {
    e.preventDefault();
    if (orderDetails.productGroupId) {
      // Validate required fields before adding to order list
      if (!orderDetails.priceType || !orderDetails.priceTypeValue) {
        toast.error("Please select price type and enter price value");
        return;
      }

      setOrderList([
        ...orderList,
        {
          ...orderDetails,
          weightMin: orderDetails.weightMax, // Use max value as both min and max for API
          priceMin: expectedPrice.max,
          priceMax: expectedPrice.max,
        },
      ]);
      setOrderDetails({
        productGroupId: "",
        size: "",
        weightMin: "",
        weightMax: "",
        priceMin: "",
        priceMax: "",
        deliveryDate: "",
        addOns: "",
        designPhoto: null,
        metal: "",
        purity: "",
        priceType: "",
        priceTypeValue: "",
      }); // Reset form fields after adding
      setExpectedPrice({ min: "", max: "" });
      setIsManualExpectedPrice(false);
      setShowFields(false);
    }
  };

  // Get product group name from its ID
  const getProductGroupName = (groupId) => {
    if (!groupId) return "Unknown Group";

    // First try direct mapping
    if (productGroups[groupId]) {
      return productGroups[groupId];
    }

    // Try with string conversion in case of type mismatches
    const stringId = String(groupId);
    if (productGroups[stringId]) {
      return productGroups[stringId];
    }

    // Fallback
    return `Group ${groupId}`;
  };

  // Prefer API product_group_name; fall back to local id → name map
  const resolveProductGroupLabel = (product) => {
    const fromApi =
      product?.product_group_name ||
      product?.productGroupName ||
      product?.product_group_label;
    if (fromApi) return fromApi;
    return getProductGroupName(
      product?.product_group_id ?? product?.product_group,
    );
  };

  // Add a function to handle Sale Not Completed form submission
  const handleSaleNotCompletedSubmit = async (e) => {
    e.preventDefault();

    if (
      !saleNotCompletedForm.reason ||
      !saleNotCompletedForm.requirements ||
      !saleNotCompletedForm.response_date
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setSubmitting(true);

      const response = await postSaleNotCompleted({
        type: "create",
        user_id: getCustomerUserId(selectedCustomer),
        by_user: userData?.id ?? userData?.user_id ?? userData?.userId,
        reason: saleNotCompletedForm.reason,
        requirements: saleNotCompletedForm.requirements,
        response_date: saleNotCompletedForm.response_date,
        feedback: saleNotCompletedForm.feedback || "",
      });
      // console.log(
      //   "Sale Not Completed submission response node:",
      //   response.data
      // );

      if (
        response &&
        (response.success === true || response.message === "radha")
      ) {
        // Update customer status to 0 (Deactive)
        if (selectedCustomer) {
          const statusResponse = await postCustomerForm({
            type: "status",
            id: selectedCustomer.id,
            status: "0",
          });

          if (statusResponse) {
            // Update local state to reflect changes
            setCustomers((prevCustomers) =>
              prevCustomers.map((customer) =>
                customer.id === selectedCustomer.id
                  ? { ...customer, status: "0" }
                  : customer,
              ),
            );
            // Reset the assigned customer status to allow assigning a new customer
            updateAssignedCustomers((prev) => {
              const updated = { ...prev };
              delete updated[selectedCustomer.id];
              return updated;
            });
          }
        }

        toast.success("Submitted successfully!");
        setShowDetailsModal(false);
        setSaleNotCompletedForm({
          reason: "",
          requirements: "",
          response_date: "",
          feedback: "",
        });
      } else {
        toast.error(response?.message || "Failed to submit record");
      }
    } catch (error) {
      console.error("Error submitting sale not completed record:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to submit record. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Function to handle input changes for Sale Not Completed form
  const handleSaleNotCompletedInputChange = (e) => {
    const { name, value } = e.target;
    setSaleNotCompletedForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to handle input changes for Repairing Order form
  const handleRepairingOrderInputChange = (e) => {
    const { name, value } = e.target;
    setRepairingOrderForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Function to add repairing order to list
  const handleAddRepairingOrder = (e) => {
    e.preventDefault();

    if (
      !repairingOrderForm.productGroupId ||
      !repairingOrderForm.size ||
      !repairingOrderForm.issue_description ||
      !repairingOrderForm.current_weight ||
      !repairingOrderForm.exp_delivery
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    // Validate price type if expected price is provided
    if (
      repairingExpectedPrice &&
      (!repairingOrderForm.priceType || !repairingOrderForm.priceTypeValue)
    ) {
      toast.error(
        "Please select price type and enter price value for expected price calculation",
      );
      return;
    }

    setRepairingOrderList([
      ...repairingOrderList,
      {
        ...repairingOrderForm,
        expectedPrice: repairingExpectedPrice,
      },
    ]);

    // Reset form
    setRepairingOrderForm({
      productGroupId: "",
      size: "",
      issue_description: "",
      designPhoto: null,
      current_weight: "",
      expected_weight: "",
      weightMin: "",
      weightMax: "",
      priceType: "",
      priceTypeValue: "",
      exp_delivery: "",
      metal: "",
      purity: "",
    });
    setRepairingExpectedPrice("");
    setIsManualPriceEntry(false);
  };

  // Function to submit all repairing orders
  const handleSubmitRepairingOrders = async () => {
    if (repairingOrderList.length === 0) {
      toast.error("Please add at least one repairing order.");
      return;
    }

    try {
      setSubmitting(true);

      const userData = getUserData();
      const formData = new FormData();
      const entries = [];

      // Add all repairing orders to entries
      repairingOrderList.forEach((order, idx) => {
        let designPhotoKey = "";
        if (order.designPhoto) {
          designPhotoKey = `image_${idx}`;
          formData.append(designPhotoKey, order.designPhoto);
        }

        entries.push({
          customer_form_id: selectedCustomer.id,
          pro_id: 0,
          page_source: "REPAIR",
          status: 1,
          order_number: "",
          product_group: order.productGroupId,
          size: order.size,
          description: order.issue_description, // Changed from issue_description to description
          current_weight: order.current_weight,
          expected_weight: order.expected_weight || "",
          weight_min: order.weightMin || "",
          weight_max: order.weightMax || "",
          price_type: order.priceType || "",
          price_value: order.priceTypeValue || "",
          price_min: order.expectedPrice || "",
          expected_price: order.expectedPrice || "",
          delivery_date: order.exp_delivery,
          design_photos: designPhotoKey, // This will be handled by the API
        });
      });

      // console.log("Submitting repairing orders entries:", entries);

      // Handle multipart vs JSON based on whether files are present
      let response;
      const hasFiles = formData.entries().next().value;

      if (hasFiles) {
        // Has files - send as multipart FormData
        const formDataForApi = new FormData();

        // Add all image files with correct keys
        for (let pair of formData.entries()) {
          if (pair[1] instanceof File) {
            formDataForApi.append(pair[0], pair[1]);
          }
        }

        // Add required fields for multipart request
        formDataForApi.append("type", "create");
        // formDataForApi.append("by_user", getUserData().id || "");
        formDataForApi.append("entries", JSON.stringify(entries));

        // console.log("=== ORDER_QUEUE API PAYLOAD (Repairing Orders - Multipart) ==="
        // console.log("Entries being sent:", entries);
        // console.log(
        //   "FormData files:",
        //   [...formData.entries()]
        //     .filter((pair) => pair[1] instanceof File)
        //     .map((pair) => pair[0])
        // );

        response = await postOrderQueueMultipart(formDataForApi);
      } else {
        const payload = {
          type: "create",
          entries: entries,
        };

        response = await postOrderQueue(payload);
      }

      if (
        response &&
        (response.success === true ||
          (Array.isArray(response.data) &&
            response.data.every((result) => result.status === "success")) ||
          (Array.isArray(response) &&
            response.every((result) => result.status === "success")))
      ) {
        // Update customer status to 0 (Deactive)
        const statusResponse = await postCustomerForm({
          type: "status",
          id: selectedCustomer.id,
          status: "0",
        });

        if (statusResponse) {
          setCustomers((prevCustomers) =>
            prevCustomers.map((customer) =>
              customer.id === selectedCustomer.id
                ? { ...customer, status: "0" }
                : customer,
            ),
          );
          updateAssignedCustomers((prev) => {
            const updated = { ...prev };
            delete updated[selectedCustomer.id];
            return updated;
          });
        }

        toast.success("All repairing orders submitted successfully!");
        setShowDetailsModal(false);
        setRepairingOrderList([]);
      } else {
        toast.error(response?.message || "Failed to submit repairing orders");
      }
    } catch (error) {
      console.error("Error submitting repairing orders:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to submit repairing orders. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageSelection = (file, orderIndex = null) => {
    if (orderIndex !== null) {
      // For existing orders in the list
      setOrderList((prevOrders) =>
        prevOrders.map((order, index) =>
          index === orderIndex ? { ...order, designPhoto: file } : order,
        ),
      );
    } else {
      // For the current order being created
      setOrderDetails({
        ...orderDetails,
        designPhoto: file,
      });
    }
  };

  const handleCombinedSubmit = async () => {
    if (selectedProducts.length === 0 && orderList.length === 0) {
      toast.error("Please select at least one product or add a custom order.");
      return;
    }

    try {
      setSubmitting(true);

      // Get the currently logged-in user data
      const userData = getUserData();

      // Prepare entries array for the combined API
      const entries = [];
      const formData = new FormData();
      let imageIndex = 0;

      // Add custom orders entries (with image handling)
      if (orderList.length > 0) {
        orderList.forEach((order, idx) => {
          let designPhotoKey = "";
          if (order.designPhoto) {
            designPhotoKey = `image_${idx}`;
            formData.append(designPhotoKey, order.designPhoto);
            imageIndex++;
          }
          entries.push({
            customer_form_id: selectedCustomer.id,
            pro_id: 0,
            page_source: "COPRO",
            status: 1,
            order_number: "",
            product_group: order.productGroupId,
            description: order.addOns || "",
            size: order.size,
            weight_min: order.weightMin,
            expected_weight: order.weightMax,
            price_type: order.priceType || "",
            price_value: order.priceTypeValue || "",
            price_min: order.priceMin,
            expected_price: order.priceMax,
            delivery_date: order.deliveryDate,
            design_photos: designPhotoKey, // This will be handled by the API
          });
        });
      }

      // Add ready products entries (no image)
      if (selectedProducts.length > 0) {
        selectedProducts.forEach((product) => {
          entries.push({
            // by_user: userData?.id || "",
            customer_form_id: selectedCustomer.id, // Changed from user_id to customer_form_id
            pro_id: product.id,
            page_source: "READYPRO",
            status: 1, // Changed from 0 to 1 to match backend expectations
            order_number: "",
            product_group:
              product.product_group_id ?? product.product_group ?? "",
            description: "",
            size: "",
            weight_min: "",
            expected_weight: "",
            price_type: "",
            price_value: "",
            price_min: "",
            expected_price: "",
            delivery_date: "",
            design_photos: "",
          });
        });
      }

      // For your Node.js API, handle multipart vs JSON based on whether files are present
      let response;
      const hasFiles = formData.entries().next().value;

      if (hasFiles) {
        // Has files - send as multipart FormData
        const formDataForApi = new FormData();

        // Add all image files with correct keys
        for (let pair of formData.entries()) {
          if (pair[1] instanceof File) {
            formDataForApi.append(pair[0], pair[1]);
          }
        }

        // Add required fields for multipart request
        formDataForApi.append("type", "create");
        // formDataForApi.append("by_user", getUserData().id || "");
        formDataForApi.append("entries", JSON.stringify(entries));

        // console.log("=== ORDER_QUEUE API PAYLOAD (Multipart) ===");
        // console.log("Entries being sent:", entries);
        // console.log(
        //   "FormData files:",
        //   [...formData.entries()]
        //     .filter((pair) => pair[1] instanceof File)
        //     .map((pair) => pair[0])
        // );

        response = await postOrderQueueMultipart(formDataForApi);
      } else {
        const payload = {
          type: "create",
          entries: entries,
        };

        response = await postOrderQueue(payload);
      }

      // Log response details to diagnose issues
      // console.log(
      //   "%c 🎯 API Response Success! ",
      //   "background: #4CAF50; color: white; font-size: 16px;"
      // );
      // console.log("Full response:", response);
      // console.log("Response data:", response.data);

      // console.log("Combined API response:", response.data);

      // Check if the API call was successful
      // console.log("Response data structure:", response.data);

      // Adjust check for different response formats
      if (
        response &&
        (response.success === true ||
          (Array.isArray(response.data) &&
            response.data.every((result) => result.status === "success")) ||
          (Array.isArray(response) &&
            response.every((result) => result.status === "success")))
      ) {
        const statusResponse = await postCustomerForm({
          type: "status",
          id: selectedCustomer.id,
          status: "0",
        });

        if (statusResponse) {
          // Update local state to reflect changes
          setCustomers((prevCustomers) =>
            prevCustomers.map((customer) =>
              customer.id === selectedCustomer.id
                ? { ...customer, status: "0" }
                : customer,
            ),
          );
          // Reset the assigned customer status
          updateAssignedCustomers((prev) => {
            const updated = { ...prev };
            delete updated[selectedCustomer.id];
            return updated;
          });
        }

        setShowDetailsModal(false);
        toast.success("All orders submitted successfully!");
        setSelectedProducts([]);
        setOrderList([]);
      } else if (response && response.success === false) {
        const errorMessage =
          response.message || "Failed to submit orders. Please try again.";
        toast.error(errorMessage);
      } else {
        const errorResult =
          (Array.isArray(response) &&
            response.find((result) => result.status === "error")) ||
          (Array.isArray(response?.data) &&
            response.data.find((result) => result.status === "error"));

        const errorMessage =
          errorResult?.message || "Some orders failed to submit.";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error submitting orders:", error);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to submit orders. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Helper functions for price calculation (from CustomOrderEntry)
  const calculatePerGramRate = (rate, metalType) => {
    // Gold rates are per 10g; Silver rates are per 1000g
    if (!rate) return 0;
    return metalType === "Silver" ? rate / 1000 : rate / 10;
  };
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate;
    if (metalType === "Gold") {
      return rate * (purityValue / 24);
    } else if (metalType === "Silver") {
      return (rate * purityValue) / 999;
    }
    return rate;
  };
  // Calculate expected price
  const calculateExpectedPrice = (
    weight,
    priceType,
    priceTypeValue,
    metal,
    purity,
  ) => {
    // console.log('=== calculateExpectedPrice called ===');
    // console.log('Input parameters:', { weight, priceType, priceTypeValue, metal, purity });

    // For per_rate and fixed, metal is not required; for percentage it is
    if (!weight || !priceType || !priceTypeValue) {
      // console.log('Missing required parameters, returning empty string');
      return "";
    }

    const w = parseFloat(weight);
    const v = parseFloat(priceTypeValue);
    // console.log('Parsed values:', { w, v });

    if (isNaN(w) || isNaN(v)) {
      // console.log('Invalid numeric values, returning empty string');
      return "";
    }

    let baseRate = null;
    let perGramRate = null;
    let purityRate = null;
    if (priceType === "percentage") {
      // Percentage calculation requires knowing the metal to derive purity-based rate
      if (!metal) {
        // console.log('Metal required for percentage calculation, returning empty string');
        return "";
      }
      baseRate = metal === "Gold" ? masterGoldRate : masterSilverRate;
      // console.log('Base rate calculation:', { metal, baseRate, masterGoldRate, masterSilverRate });
      if (!baseRate || baseRate === 0) {
        // console.log('Base rate is 0 or undefined, returning empty string');
        return "";
      }
      perGramRate = calculatePerGramRate(baseRate, metal);
      purityRate = calculatePurityBasedRate(purity, perGramRate, metal);
      // console.log('Rate calculations:', { baseRate, perGramRate, purityRate });
    }

    let result = "";
    if (priceType === "percentage") {
      result = (w * purityRate * (v / 100)).toFixed(2);
      // console.log('Percentage calculation:', `${w} * ${purityRate} * (${v} / 100) = ${result}`);
    } else if (priceType === "per_rate") {
      result = (w * v).toFixed(2);
      // console.log('Per rate calculation:', `${w} * ${v} = ${result}`);
    } else if (priceType === "fixed") {
      result = v.toFixed(2);
      // console.log('Fixed calculation:', v);
    }

    // console.log('Final result:', result);
    return result;
  };
  // Auto-calculate expected price when relevant fields change
  useEffect(() => {
    if (!isManualExpectedPrice) {
      const max = calculateExpectedPrice(
        orderDetails.weightMax,
        orderDetails.priceType,
        orderDetails.priceTypeValue,
        orderDetails.metal,
        orderDetails.purity,
      );
      setExpectedPrice({ min: "", max });
    }
  }, [
    orderDetails.weightMax,
    orderDetails.priceType,
    orderDetails.priceTypeValue,
    orderDetails.metal,
    orderDetails.purity,
    masterGoldRate,
    masterSilverRate,
    isManualExpectedPrice,
  ]);

  // Auto-calculate expected price for repairing order
  useEffect(() => {
    if (!isManualPriceEntry) {
      // console.log('=== REPAIRING ORDER PRICE CALCULATION ===');

      const price = calculateExpectedPrice(
        repairingOrderForm.expected_weight,
        repairingOrderForm.priceType,
        repairingOrderForm.priceTypeValue,
        repairingOrderForm.metal,
        repairingOrderForm.purity,
      );

      // console.log('Calculated price:', price);
      // console.log('Setting repairingExpectedPrice to:', price);
      setRepairingExpectedPrice(price);
    }
  }, [
    repairingOrderForm.expected_weight,
    repairingOrderForm.priceType,
    repairingOrderForm.priceTypeValue,
    repairingOrderForm.metal,
    repairingOrderForm.purity,
    masterGoldRate,
    masterSilverRate,
    isManualPriceEntry,
  ]);

  // Handler functions for add customer form
  const handleMobileChange = (option) => {
    const sanitizedOption = option
      ? { ...option, value: sanitizeMobileInput(option.value), label: sanitizeMobileInput(option.value) }
      : null;
    setSelectedMobile(sanitizedOption);
    setMobileNo(sanitizedOption ? sanitizedOption.value : "");
    setMobileInputValue("");
    if (option && option.name) {
      setSelectedName({
        value: option.name,
        label: option.name,
        mobile: option.value,
      });
      setFullName(option.name);
    } else if (option && option.__isNew__) {
      if (!fullName) {
        setSelectedName(null);
        setFullName("");
      }
    } else {
      setSelectedName(null);
      setFullName("");
    }
  };

  const handleNameChange = (option) => {
    setSelectedName(option);
    setFullName(option ? option.value : "");
    if (option && option.mobile) {
      setSelectedMobile({
        value: option.mobile,
        label: option.mobile,
        name: option.value,
      });
      setMobileNo(option.mobile);
    } else if (option && option.__isNew__) {
      if (!mobileNo) {
        setSelectedMobile(null);
        setMobileNo("");
      }
    } else {
      setSelectedMobile(null);
      setMobileNo("");
    }
  };

  const handleAddCustomerSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!fullName.trim()) {
      toast.error("Please enter your full name");
      return;
    }

    if (!isValidMobileNumber(mobileNo)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    if (assignMobileExistsCheck.exists && !isExistingCustomerSelected) {
      toast(
        "This mobile is already registered — walk-in will be linked to the existing customer.",
        { icon: "ℹ️" },
      );
    }

    try {
      setSubmitting(true);

      // Prepare payload for self_customer_form API
      const payload = {
        type: "create",
        name: fullName,
        mobile: mobileNo,
        state: state || undefined,
        by_user: userData?.id ?? userData?.user_id ?? userData?.userId,
        purpose_of_visit: purposeOfVisit || "",
        no_of_visitors: noOfVisitors || 0,
        function_purpose: functionPurpose || "",
        function_date: functionDate || "",
        scheme: scheme,
        instagram: instagramFollow,
        about: reference || "",
      };

      // console.log("Payload being sent to self_customer_form API:", payload);

      const response = await postSelfCustomerForm(payload);

      if (response && response.success) {
        // (console.log("Response from API:", response.data));
        // Fetch the newly created customer
        const customersResponse = await postCustomerForm({
          type: "show",
        });

        if (
          isErpApiSuccess(customersResponse) ||
          customersResponse?.message === "radha"
        ) {
          const customersData = customersResponse.data || [];
          const newestCustomer = customersData.sort(
            (a, b) => parseInt(b.id) - parseInt(a.id),
          )[0];

          if (newestCustomer && newestCustomer.id) {
            // Upsert — socket may have already added this row
            setCustomers((prevCustomers) => {
              const exists = prevCustomers.some(
                (c) => String(c.id) === String(newestCustomer.id),
              );
              const nextRow = { ...newestCustomer, status: "2" };
              if (exists) {
                return prevCustomers
                  .map((c) =>
                    String(c.id) === String(newestCustomer.id)
                      ? { ...c, ...nextRow }
                      : c,
                  )
                  .sort((a, b) => parseInt(b.id) - parseInt(a.id));
              }
              return [...prevCustomers, nextRow].sort(
                (a, b) => parseInt(b.id) - parseInt(a.id),
              );
            });

            updateAssignedCustomers((prev) => ({
              ...prev,
              [newestCustomer.id]: true,
            }));

            toast.success("Customer added and assigned successfully!");
            setShowAddCustomerModal(false);

            // Reset form fields
            setFullName("");
            setMobileNo("");
            setSelectedMobile(null);
            setSelectedName(null);
            const gujarat = states.find((s) => s.name === "Gujarat");
            if (gujarat) {
              setState(gujarat.name);
              setSelectedState(gujarat.iso2);
            } else {
              setState("Gujarat");
              setSelectedState("GJ");
            }
            setPurposeOfVisit("");
            setNoOfVisitors(1);
            setFunctionPurpose("");
            setFunctionDate("");
            setScheme("No");
            setInstagramFollow("No");
            setReference("");
          }
        }
      } else {
        toast.error(response?.message || "Failed to add customer");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to add customer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseAddCustomerModal = () => {
    setShowAddCustomerModal(false);
    // Reset form fields
    setFullName("");
    setMobileNo("");
    setSelectedMobile(null);
    setSelectedName(null);
    setMobileInputValue("");
    setNameInputValue("");
    setMobileOptions([]);
    setNameOptions([]);
    const gujarat = states.find((s) => s.name === "Gujarat");
    if (gujarat) {
      setState(gujarat.name);
      setSelectedState(gujarat.iso2);
    } else {
      setState("Gujarat");
      setSelectedState("GJ");
    }
    setPurposeOfVisit("");
    setNoOfVisitors(1);
    setFunctionPurpose("");
    setFunctionDate("");
    setScheme("No");
    setInstagramFollow("No");
    setReference("");
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        {/* <div className="mb-3 d-flex gap-4">
          <p className="mb-0">
            Master Gold Rate:{" "}
            <b>
              ₹
              {masterGoldRate?.toFixed
                ? masterGoldRate.toFixed(2)
                : masterGoldRate}
            </b>
          </p>
          <p className="mb-0">
            Master Silver Rate:{" "}
            <b>
              ₹
              {masterSilverRate?.toFixed
                ? masterSilverRate.toFixed(2)
                : masterSilverRate}
            </b>
          </p>
        </div> */}
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Assign Customer</h4>
            <Button
              variant="primary"
              className={buttonClass}
              onClick={() => setShowAddCustomerModal(true)}
            >
              Assign to Self
            </Button>
          </div>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3 flex-wrap">
              <Form.Control
                type="text"
                placeholder="Search..."
                style={{ maxWidth: "260px" }}
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className={buttonTableStyle === "square" ? "rounded-0" : ""}
              />
              <Form.Select
                style={{ maxWidth: "200px" }}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className={buttonTableStyle === "square" ? "rounded-0" : ""}
              >
                <option value="">All Status</option>
                <option value="Assigned">Assigned</option>
                <option value="Unassigned">Unassigned</option>
                <option value="Completed">Completed</option>
              </Form.Select>
            </div>
            {loading ? (
              <div className="text-center">Loading customers...</div>
            ) : (
              <>
                <div className={`table-responsive mb-4 border ${cardClass}`}>
                  <table
                    className={`table text-nowrap mb-0 align-middle ${tableClass}`}
                  >
                    <thead className="text-dark fs-4">
                      <tr>
                        <th>Customer</th>
                        <th>Mobile No</th>
                        <th>Token</th>
                        <th>Requirement</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedCustomers.length > 0 ? (
                        displayedCustomers.map((customer, index) => {
                          const userName = getCustomerDisplayName(customer);
                          const userMobile = getCustomerDisplayMobile(customer);
                          const rowStatusClass = getStatusRowClass(customer.status);
                          return (
                            <tr key={customer.id} className={rowStatusClass}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {/* <img
                                    src={`../assets/images/profile/user-${
                                      (index % 3) + 3
                                    }.jpg`}
                                    className="rounded-circle"
                                    width={40}
                                    height={40}
                                    alt="User"
                                  /> */}
                                  <div className="ms-3">
                                    <h6 className="fs-4 fw-semibold mb-0">
                                      {userName}
                                    </h6>
                                  </div>
                                </div>
                              </td>
                              <td>{userMobile}</td>
                              <td>
                                <span
                                  className={`badge ${statusColors.secondary}`}
                                  style={{ fontSize: "1em" }}
                                >
                                  {customer.token_number
                                    ? customer.token_number
                                    : customer.id}
                                </span>
                              </td>
                              <td>{customer.purpose_of_visit}</td>
                              <td>
                                <span
                                  className={`badge ${customer.status === 2 ||
                                    customer.status === "2"
                                    ? "bg-success-subtle text-success"
                                    : customer.status === 1 ||
                                      customer.status === "1"
                                      ? "bg-warning-subtle text-warning"
                                      : "bg-danger-subtle text-danger"
                                    }`}
                                >
                                  {customer.status === 2 ||
                                    customer.status === "2"
                                    ? "Assigned"
                                    : customer.status === 1 ||
                                      customer.status === "1"
                                      ? "Unassigned"
                                      : "Completed"}
                                </span>
                              </td>
                              <td>
                                {(() => {
                                  const tokenNumber =
                                    customer.token_number || customer.id;
                                  const isAssignedToCurrentUser =
                                    !!assignedCustomers[customer.id];
                                  const isAssignedStatus =
                                    customer.status === 2 ||
                                    customer.status === "2";
                                  const isCompleted =
                                    customer.status === 0 ||
                                    customer.status === "0";

                                  return (
                                    <>
                                      {isAssignedToCurrentUser ? (
                                        <button
                                          className={`btn btn-success me-2 ${buttonClass}`}
                                          disabled
                                        >
                                          Assigned
                                        </button>
                                      ) : isAssignedStatus ? (
                                        <button
                                          className={`btn btn-secondary me-2 ${buttonClass}`}
                                          disabled
                                        >
                                          Assigned
                                        </button>
                                      ) : (
                                        <button
                                          className={`btn btn-primary me-2 ${buttonClass}`}
                                          onClick={() =>
                                            handleAssignClick(customer)
                                          }
                                          disabled={isCompleted || isAssigned}
                                        >
                                          Assign Me
                                        </button>
                                      )}
                                      {isAssignedToCurrentUser && (
                                        <button
                                          className={`btn btn-secondary ${buttonClass}`}
                                          onClick={() =>
                                            handleAddDetailsClick(customer)
                                          }
                                        >
                                          Add Details
                                        </button>
                                      )}
                                    </>
                                  );
                                })()}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No customers found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  totalItems={filteredCustomers.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  itemsPerPageOptions={[10, 20, 30, 50, 100]}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Body className="text-center">
          <h5>Are you sure you want to assign this customer?</h5>
          <p>{selectedCustomer?.name}</p>
          <div className="mt-4">
            <Button
              variant="primary"
              className={`me-2 ${buttonClass}`}
              onClick={handleConfirmAssign}
            >
              Yes
            </Button>
            <Button
              variant="secondary"
              className={buttonClass}
              onClick={handleClose}
            >
              No
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showWarningModal}
        onHide={handleClose}
        centered
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Body className="text-center">
          <h5>Assignment Restricted</h5>
          <p>You can only assign one customer at a time.</p>
          <div className="mt-4">
            <Button
              variant="primary"
              className={buttonClass}
              onClick={handleClose}
            >
              OK
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showDetailsModal}
        onHide={handleClose}
        centered
        size="xl"
        dialogClassName="custom-modal-width"
        style={{ zIndex: 1055 }}
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Body style={{ overflow: "hidden" }}>
          <h5 className="text-center">Add Details</h5>
          {/* <div className="mb-3 d-flex gap-4 justify-content-center">
            <p className="mb-0">
              Master Gold Rate:{" "}
              <b>
                ₹
                {masterGoldRate?.toFixed
                  ? masterGoldRate.toFixed(2)
                  : masterGoldRate}
              </b>
            </p>
            <p className="mb-0">
              Master Silver Rate:{" "}
              <b>
                ₹
                {masterSilverRate?.toFixed
                  ? masterSilverRate.toFixed(2)
                  : masterSilverRate}
              </b>
            </p>
          </div> */}
          <div
            className={`border ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
              } p-3`}
          >
            <div className="d-flex justify-content-center mb-3">
              <div className="d-inline-flex overflow-hidden rounded-pill border bg-white shadow-sm">
                {[
                  ["sellCompleted", "Sale Completed"],
                  ["sellNotCompleted", "Sale Not Completed"],
                  ["repairingOrder", "Repairing Order"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    className={`btn border-0 rounded-0 px-4 py-2 ${detailsTab === key
                      ? "btn-primary text-white"
                      : "bg-white text-dark"
                      } ${buttonClass}`}
                    style={{ minWidth: "140px", boxShadow: "none" }}
                    onClick={() => setDetailsTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Tabs
              activeKey={detailsTab}
              onSelect={(key) => key && setDetailsTab(key)}
              id="details-tabs"
              className="d-none"
            >
              <Tab
                eventKey="sellCompleted"
                title="Sale Completed"
              >
                <div
                  className="d-flex flex-column flex-lg-row my-4"
                  style={{ overflow: "hidden" }}
                >
                  {/* Left Section */}
                  <div
                    className="flex-grow-1 pe-lg-3 w-100 w-lg-50"
                    style={{ overflow: "auto", maxHeight: "70vh" }}
                  >
                    <h5
                      className="text-center mt-3"
                      style={{ color: "#98A4AE" }}
                    >
                      Proceed with{" "}
                      <span className="text-black font-bold">
                        Ready Products
                      </span>
                    </h5>

                    <div className="position-relative mb-3">
                      <Form.Control
                        type="text"
                        placeholder="Search for a product by design, tag number or product group..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        className={`border ${buttonTableStyle === "square"
                          ? "rounded-0"
                          : "rounded"
                          }`}
                      />
                      {productsLoading ? (
                        <div className="text-center">Loading products...</div>
                      ) : (
                        searchTerm && (
                          <div
                            className={`position-absolute w-100 bg-white border ${buttonTableStyle === "square"
                              ? "rounded-0"
                              : "rounded"
                              } shadow-lg`}
                            style={{
                              zIndex: 1000,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            {products.map((product) => (
                              <div
                                key={product.id}
                                className="p-2 border-bottom cursor-pointer hover-bg-light"
                                onClick={() => {
                                  handleProductSelect(product);
                                  setSearchTerm("");
                                }}
                                style={{ cursor: "pointer" }}
                                onMouseEnter={(e) =>
                                  (e.target.style.backgroundColor = "#f8f9fa")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.backgroundColor = "white")
                                }
                              >
                                <div className="fw-bold">
                                  {resolveProductGroupLabel(product)}
                                </div>
                                <div className="text-muted small">
                                  Tag: {product.tag_no || "N/A"} | NWt:{" "}
                                  {product.nwt ?? product.NWT ?? "N/A"} g
                                </div>
                              </div>
                            ))}
                            {!productsLoading && products.length === 0 && (
                              <div className="p-2 text-muted">
                                No products found matching "{searchTerm}"
                              </div>
                            )}
                          </div>
                        )
                      )}
                    </div>

                    {selectedProducts.length > 0 && (
                      <div
                        className={`table-responsive border ${buttonTableStyle === "square"
                          ? "rounded-0"
                          : "rounded-1"
                          }`}
                        style={{
                          maxHeight:
                            selectedProducts.length > 2 ? "300px" : "none",
                          overflowY: "auto",
                        }}
                      >
                        <table className="table text-nowrap mb-0 align-middle">
                          <tbody>
                            {selectedProducts.map((product) => (
                              <tr key={product.id}>
                                <td className="p-3">
                                  <img
                                    src={getProductImageUrl(product.image)}
                                    alt={product.design || "Product"}
                                    className="rounded"
                                    style={{
                                      width: "80px",
                                      height: "80px",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null; // Prevent infinite loop
                                      e.target.src =
                                        "https://i.ibb.co/rKZrvh64/jewellery-showcase.png";
                                    }}
                                  />
                                </td>
                                <td>
                                  <div>
                                    <strong>Product Group:</strong>{" "}
                                    {resolveProductGroupLabel(product)}
                                  </div>
                                  <div>
                                    <strong>Tag No:</strong>{" "}
                                    {product.tag_no || "N/A"}
                                  </div>
                                  <div>
                                    <strong>Net Weight:</strong>{" "}
                                    {product.nwt ?? product.NWT ?? "N/A"} g
                                  </div>
                                  <hr className="my-2" />
                                  <div>
                                    <strong>Gross Weight:</strong>{" "}
                                    {product.gwt ?? product.GWT ?? "N/A"} g
                                  </div>
                                  <div>
                                    <strong>Size:</strong>{" "}
                                    {product.size || "N/A"}
                                  </div>
                                  {/* <div>
                                    <strong>Price:</strong> ₹
                                    {product.MRP || product.LBR_amt || "N/A"}
                                  </div> */}
                                </td>
                                <td className="text-end align-top">
                                  <Icon
                                    icon="mdi:close"
                                    width={24}
                                    height={24}
                                    onClick={() =>
                                      handleRemoveProduct(product.id)
                                    }
                                    title="Remove product"
                                    style={{
                                      color: "#6c757d",
                                      cursor: "pointer",
                                      transition: "color 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.color = "#dc3545";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.color = "#6c757d";
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="mb-3 d-none">
                      <Form.Label>
                        <strong>Page Source</strong>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value="READYPRO"
                        readOnly
                        className="bg-light"
                      />
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                      <Button
                        variant="secondary"
                        className={buttonClass}
                        onClick={() => navigate("/addstock")}
                      >
                        Create Product
                      </Button>
                    </div>
                  </div>

                  {/* Separation line with "OR" text (Hidden on laptop view) */}
                  <div className="d-lg-none d-flex align-items-center justify-content-center my-4">
                    <hr className="flex-grow-1" style={{ margin: 0 }} />
                    <span className="mx-2 px-3 text-muted fw-bold">OR</span>
                    <hr className="flex-grow-1" style={{ margin: 0 }} />
                  </div>

                  {/* Vertical Separator for Laptop */}
                  <div className="d-none d-lg-flex flex-column align-items-center justify-content-center mx-3">
                    <div
                      style={{
                        width: "1px",
                        flex: 1,
                        backgroundColor: "#dee2e6",
                      }}
                    />
                    <span className="my-2 px-2 text-muted fw-bold">OR</span>
                    <div
                      style={{
                        width: "1px",
                        flex: 1,
                        backgroundColor: "#dee2e6",
                      }}
                    />
                  </div>

                  {/* Customize Product Section */}
                  <div
                    className="flex-grow-1 ps-lg-3 w-100 w-lg-50"
                    style={{
                      overflow: "auto",
                      maxHeight: "70vh",
                      paddingRight: "10px",
                    }}
                  >
                    <h5
                      className="text-center mt-3"
                      style={{ color: "#98A4AE" }}
                    >
                      Proceed with{" "}
                      <span className="text-black font-bold">Custom Order</span>
                    </h5>
                    <Form className="mt-3" onSubmit={handleAddToOrders}>
                      <Form.Group className="mb-3">
                        <Form.Label>Product Group </Form.Label>
                        <Select
                          options={productGroupOptions}
                          value={
                            productGroupOptions.find(
                              (opt) =>
                                String(opt.value) ===
                                String(orderDetails.productGroupId || ""),
                            ) ||
                            (orderDetails.productGroupId
                              ? {
                                value: orderDetails.productGroupId,
                                label: getProductGroupName(
                                  orderDetails.productGroupId,
                                ),
                              }
                              : null)
                          }
                          onChange={async (selectedOption) => {
                            const selectedId = selectedOption?.value || "";
                            setShowFields(!!selectedId);
                            rememberProductGroupSelection(selectedOption);
                            let metal = selectedOption?.metal || "";
                            let purity = selectedOption?.purity || "";
                            if (selectedOption && (!metal || !purity)) {
                              try {
                                const enriched =
                                  await enrichProductGroupSelectOption(
                                    selectedOption,
                                  );
                                metal = enriched?.metal || metal;
                                purity = enriched?.purity || purity;
                              } catch {
                                /* keep empty */
                              }
                            }
                            setOrderDetails((prev) => ({
                              ...prev,
                              productGroupId: selectedId,
                              metal,
                              purity,
                            }));
                            setIsManualExpectedPrice(false);
                          }}
                          onInputChange={(inputValue, { action }) => {
                            if (action === "input-change") {
                              scheduleProductGroupSearch(inputValue);
                            }
                            return inputValue;
                          }}
                          filterOption={null}
                          isLoading={productGroupSearchLoading}
                          noOptionsMessage={({ inputValue }) =>
                            String(inputValue || "").trim().length < 3
                              ? "Type at least 3 characters..."
                              : productGroupSearchLoading
                                ? "Searching..."
                                : "No options"
                          }
                          placeholder="Search product group (min 3 chars)"
                          isClearable
                          isSearchable
                          styles={selectStyles}
                        />
                      </Form.Group>
                      {/* Show metal and purity fields (read-only) if set */}
                      {/* {orderDetails.metal && (
                        <Form.Group className="mb-3">
                          <Form.Label>Metal</Form.Label>
                          <Form.Control
                            type="text"
                            value={orderDetails.metal}
                            readOnly
                          />
                        </Form.Group>
                      )}
                      {orderDetails.purity && (
                        <Form.Group className="mb-3">
                          <Form.Label>Purity</Form.Label>
                          <Form.Control
                            type="text"
                            value={orderDetails.purity}
                            readOnly
                          />
                        </Form.Group>
                      )} */}

                      {showFields && (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label>Add-ons</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter any additional features or requirements"
                              value={orderDetails.addOns || ""}
                              onChange={(e) =>
                                setOrderDetails({
                                  ...orderDetails,
                                  addOns: e.target.value,
                                })
                              }
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Design Photo</Form.Label>
                            <Form.Control
                              type="file"
                              onChange={(e) =>
                                handleImageSelection(e.target.files[0])
                              }
                              accept="image/*"
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Size </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter the size"
                              required
                              value={orderDetails.size || ""}
                              onChange={(e) =>
                                setOrderDetails({
                                  ...orderDetails,
                                  size: e.target.value,
                                })
                              }
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>
                              Estimated Weight (in grams){" "} 
                            </Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Enter maximum weight"
                              required
                              value={orderDetails.weightMax || ""}
                              onChange={(e) =>
                                setOrderDetails({
                                  ...orderDetails,
                                  weightMax: e.target.value,
                                })
                              }
                              onInput={(e) =>
                              (e.target.value = e.target.value.replace(
                                /[^0-9.]/g,
                                "",
                              ))
                              }
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          {/* New Price Type Field */}
                          <Form.Group className="mb-3">
                            <Form.Label>Price Type </Form.Label>
                            <div className="input-group">
                              <Form.Select
                                style={{ maxWidth: "150px" }}
                                value={orderDetails.priceType || ""}
                                onChange={(e) =>
                                  setOrderDetails({
                                    ...orderDetails,
                                    priceType: e.target.value,
                                  })
                                }
                                required
                                className={
                                  buttonTableStyle === "square"
                                    ? "rounded-0"
                                    : ""
                                }
                              >
                                <option value="">Select</option>
                                <option value="percentage">
                                  Percentage (%)
                                </option>
                                <option value="per_rate">Per gm (rate)</option>
                                <option value="fixed">Fixed</option>
                              </Form.Select>
                              <Form.Control
                                type="text"
                                placeholder="Enter value"
                                value={orderDetails.priceTypeValue || ""}
                                onChange={(e) =>
                                  setOrderDetails({
                                    ...orderDetails,
                                    priceTypeValue: e.target.value,
                                  })
                                }
                                required
                                className={
                                  buttonTableStyle === "square"
                                    ? "rounded-0"
                                    : ""
                                }
                              />
                            </div>
                          </Form.Group>
                          {/* Show expected price max (editable) */}
                          <Form.Group className="mb-3">
                            <Form.Label>Expected Price </Form.Label>
                            <Form.Control
                              type="text"
                              value={expectedPrice.max || ""}
                              onChange={(e) => {
                                setExpectedPrice({
                                  min: "",
                                  max: e.target.value,
                                });
                                setIsManualExpectedPrice(true);
                              }}
                              placeholder="Auto-calculated or enter manually"
                              onInput={(e) =>
                              (e.target.value = e.target.value.replace(
                                /[^0-9.]/g,
                                "",
                              ))
                              }
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3">
                            <Form.Label>Expected Delivery Date  </Form.Label>
                            <Form.Control
                              type="date"
                              required
                              value={orderDetails.deliveryDate || ""}
                              onChange={(e) =>
                                setOrderDetails({
                                  ...orderDetails,
                                  deliveryDate: e.target.value,
                                })
                              }
                              className={
                                buttonTableStyle === "square" ? "rounded-0" : ""
                              }
                            />
                          </Form.Group>

                          <Form.Group className="mb-3 d-none">
                            <Form.Label>
                              <strong>Page Source</strong>
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value="COPRO"
                              readOnly
                              className="bg-light"
                            />
                          </Form.Group>

                          <div className="d-flex justify-content-end mt-3">
                            <Button
                              variant="primary"
                              type="submit"
                              className={buttonClass}
                            >
                              Add to Orders
                            </Button>
                          </div>
                        </>
                      )}
                    </Form>

                    <div className="mt-4">
                      <h5>Current Orders:</h5>
                      {orderList.length > 0 ? (
                        <div
                          className={`table-responsive border ${cardClass}`}
                          style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                            overflowX: "hidden",
                          }}
                        >
                          <table
                            className="table mb-0 align-middle"
                            style={{ wordWrap: "break-word" }}
                          >
                            <tbody>
                              {orderList.map((order, index) => (
                                <tr key={index}>
                                  <td
                                    className="p-3"
                                    style={{ width: "100px" }}
                                  >
                                    {order.designPhoto && (
                                      <img
                                        src={URL.createObjectURL(
                                          order.designPhoto,
                                        )}
                                        alt={order.productName}
                                        className="rounded"
                                        style={{
                                          width: "80px",
                                          height: "80px",
                                          objectFit: "cover",
                                        }}
                                      />
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      maxWidth: "0",
                                      wordWrap: "break-word",
                                      overflowWrap: "break-word",
                                    }}
                                  >
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Product Group:</strong>{" "}
                                      {getProductGroupName(
                                        order.productGroupId,
                                      )}
                                    </div>
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Add-ons:</strong>{" "}
                                      {order.addOns || "N/A"}
                                    </div>
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Size:</strong>{" "}
                                      {order.size || "N/A"}
                                    </div>
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Weight:</strong>{" "}
                                      {order.weightMax || "N/A"} grams
                                    </div>
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Price:</strong> ₹
                                      {order.priceMax || "N/A"}
                                    </div>
                                    <div style={{ whiteSpace: "normal" }}>
                                      <strong>Delivery Date:</strong>{" "}
                                      {order.deliveryDate
                                        ? new Date(
                                          order.deliveryDate,
                                        ).toLocaleDateString("en-GB")
                                        : "N/A"}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p>No orders added yet.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Add the combined submit button at the bottom */}
                <div className="d-flex justify-content-center mt-4">
                  <Button
                    variant="primary"
                    className={buttonClass}
                    onClick={handleCombinedSubmit}
                    disabled={
                      submitting ||
                      (selectedProducts.length === 0 && orderList.length === 0)
                    }
                  >
                    {submitting ? "Submitting..." : "Submit All Orders"}
                  </Button>
                </div>
              </Tab>
              <Tab
                eventKey="sellNotCompleted"
                title="Sale Not Completed"
              >
                <div
                  className={`p-3 border ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                    }`}
                >
                  <Form onSubmit={handleSaleNotCompletedSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        What was the reason for the sale not being
                        completed?{" "} 
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter the reason..."
                        required
                        name="reason"
                        value={saleNotCompletedForm.reason}
                        onChange={handleSaleNotCompletedInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        What are the customer's requirements?{" "} 
                      </Form.Label>
                      <Form.Control
                        as="select"
                        required
                        name="requirements"
                        value={saleNotCompletedForm.requirements}
                        onChange={handleSaleNotCompletedInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      >
                        <option value="">Select a product</option>
                        <option>Ring</option>
                        <option>Bracelet</option>
                        <option>Necklace</option>
                        <option>Chain</option>
                      </Form.Control>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        When should we expect the customer's response?{" "} 
                      </Form.Label>
                      <Form.Control
                        type="date"
                        required
                        name="response_date"
                        value={saleNotCompletedForm.response_date}
                        onChange={handleSaleNotCompletedInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>What was the customer's feedback? </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Enter customer feedback..."
                        name="feedback"
                        value={saleNotCompletedForm.feedback}
                        onChange={handleSaleNotCompletedInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                      <Button
                        variant="primary"
                        type="submit"
                        className={buttonClass}
                        disabled={submitting}
                      >
                        {submitting ? "Submitting..." : "Submit"}
                      </Button>
                    </div>
                  </Form>
                </div>
              </Tab>

              <Tab
                eventKey="repairingOrder"
                title="Repairing Order"
              >
                <div
                  className={`p-3 border ${buttonTableStyle === "square" ? "rounded-0" : "rounded"
                    }`}
                >
                  <Form onSubmit={handleAddRepairingOrder}>
                    <Form.Group className="mb-3">
                      <Form.Label>Product Group </Form.Label>
                      <Select
                        options={productGroupOptions}
                        value={
                          productGroupOptions.find(
                            (opt) =>
                              String(opt.value) ===
                              String(repairingOrderForm.productGroupId || ""),
                          ) ||
                          (repairingOrderForm.productGroupId
                            ? {
                              value: repairingOrderForm.productGroupId,
                              label: getProductGroupName(
                                repairingOrderForm.productGroupId,
                              ),
                            }
                            : null)
                        }
                        onChange={async (selectedOption) => {
                          const selectedId = selectedOption?.value || "";
                          rememberProductGroupSelection(selectedOption);
                          let metal = selectedOption?.metal || "";
                          let purity = selectedOption?.purity || "";
                          if (selectedOption && (!metal || !purity)) {
                            try {
                              const enriched =
                                await enrichProductGroupSelectOption(
                                  selectedOption,
                                );
                              metal = enriched?.metal || metal;
                              purity = enriched?.purity || purity;
                            } catch {
                              /* keep empty */
                            }
                          }
                          setRepairingOrderForm((prev) => ({
                            ...prev,
                            productGroupId: selectedId,
                            metal,
                            purity,
                          }));
                          setIsManualPriceEntry(false);
                        }}
                        onInputChange={(inputValue, { action }) => {
                          if (action === "input-change") {
                            scheduleProductGroupSearch(inputValue);
                          }
                          return inputValue;
                        }}
                        filterOption={null}
                        isLoading={productGroupSearchLoading}
                        noOptionsMessage={({ inputValue }) =>
                          String(inputValue || "").trim().length < 3
                            ? "Type at least 3 characters..."
                            : productGroupSearchLoading
                              ? "Searching..."
                              : "No options"
                        }
                        isClearable
                        isSearchable
                        styles={selectStyles}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Size </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter the size"
                        required
                        name="size"
                        value={repairingOrderForm.size}
                        onChange={handleRepairingOrderInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Issue Description </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Describe the issue or repair needed..."
                        required
                        name="issue_description"
                        value={repairingOrderForm.issue_description}
                        onChange={handleRepairingOrderInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Image</Form.Label>
                      <Form.Control
                        type="file"
                        onChange={(e) =>
                          setRepairingOrderForm({
                            ...repairingOrderForm,
                            designPhoto: e.target.files[0],
                          })
                        }
                        accept="image/*"
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Current Weight (in grams) </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter current weight"
                        required
                        name="current_weight"
                        value={repairingOrderForm.current_weight}
                        onChange={handleRepairingOrderInputChange}
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          "",
                        ))
                        }
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>
                        Expected Weight After Repair (in grams)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter expected weight after repair"
                        name="expected_weight"
                        value={repairingOrderForm.expected_weight}
                        onChange={handleRepairingOrderInputChange}
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          "",
                        ))
                        }
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Price Type</Form.Label>
                      <div className="input-group">
                        <Form.Select
                          style={{ maxWidth: "150px" }}
                          name="priceType"
                          value={repairingOrderForm.priceType}
                          onChange={handleRepairingOrderInputChange}
                          className={
                            buttonTableStyle === "square" ? "rounded-0" : ""
                          }
                        >
                          <option value="">Select</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="per_rate">Per gm (rate)</option>
                          <option value="fixed">Fixed</option>
                        </Form.Select>
                        <Form.Control
                          type="text"
                          placeholder="Enter value"
                          name="priceTypeValue"
                          value={repairingOrderForm.priceTypeValue}
                          onChange={handleRepairingOrderInputChange}
                          className={
                            buttonTableStyle === "square" ? "rounded-0" : ""
                          }
                        />
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Expected Price</Form.Label>
                      <Form.Control
                        type="text"
                        value={repairingExpectedPrice || ""}
                        onChange={(e) => {
                          setRepairingExpectedPrice(e.target.value);
                          setIsManualPriceEntry(true);
                        }}
                        placeholder="Auto-calculated or enter manually"
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9.]/g,
                          "",
                        ))
                        }
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Expected Delivery Date </Form.Label>
                      <Form.Control
                        type="date"
                        required
                        name="exp_delivery"
                        value={repairingOrderForm.exp_delivery}
                        onChange={handleRepairingOrderInputChange}
                        className={
                          buttonTableStyle === "square" ? "rounded-0" : ""
                        }
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-end mt-3">
                      <Button
                        variant="primary"
                        type="submit"
                        className={buttonClass}
                      >
                        Add to Orders
                      </Button>
                    </div>
                  </Form>

                  {/* Display Added Repairing Orders */}
                  <div className="mt-4">
                    <h5>Current Repairing Orders:</h5>
                    {repairingOrderList.length > 0 ? (
                      <div
                        className="table-responsive border rounded-1"
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                      >
                        <table className="table mb-0 align-middle">
                          <tbody>
                            {repairingOrderList.map((order, index) => (
                              <tr key={index}>
                                <td className="p-3" style={{ width: "100px" }}>
                                  {order.designPhoto && (
                                    <img
                                      src={URL.createObjectURL(
                                        order.designPhoto,
                                      )}
                                      alt={`Repair ${index + 1}`}
                                      className="rounded"
                                      style={{
                                        width: "80px",
                                        height: "80px",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />
                                  )}
                                </td>
                                <td
                                  style={{
                                    wordBreak: "break-word",
                                    maxWidth: "400px",
                                  }}
                                >
                                  <div
                                    style={{
                                      whiteSpace: "normal",
                                      wordWrap: "break-word",
                                    }}
                                  >
                                    <strong>Product Group:</strong>{" "}
                                    {getProductGroupName(order.productGroupId)}
                                  </div>
                                  <div>
                                    <strong>Size:</strong> {order.size}
                                  </div>
                                  <div>
                                    <strong>Issue:</strong>{" "}
                                    {order.issue_description}
                                  </div>
                                  <div>
                                    <strong>Current Weight:</strong>{" "}
                                    {order.current_weight} g
                                  </div>
                                  {order.expected_weight && (
                                    <div>
                                      <strong>Expected Weight:</strong>{" "}
                                      {order.expected_weight} g
                                    </div>
                                  )}
                                  {order.expectedPrice && (
                                    <div>
                                      <strong>Expected Price:</strong> ₹
                                      {order.expectedPrice}
                                    </div>
                                  )}
                                  <div>
                                    <strong>Delivery Date:</strong>{" "}
                                    {order.exp_delivery
                                      ? new Date(
                                        order.exp_delivery,
                                      ).toLocaleDateString("en-GB")
                                      : "N/A"}
                                  </div>
                                </td>
                                <td className="text-end align-top">
                                  <Icon
                                    icon="mdi:close"
                                    width={24}
                                    height={24}
                                    onClick={() =>
                                      setRepairingOrderList(
                                        repairingOrderList.filter(
                                          (_, i) => i !== index,
                                        ),
                                      )
                                    }
                                    title="Remove order"
                                    style={{
                                      color: "#6c757d",
                                      cursor: "pointer",
                                      transition: "color 0.2s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.color = "#dc3545";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.color = "#6c757d";
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p>No repairing orders added yet.</p>
                    )}
                  </div>

                  {/* Submit All Orders Button */}
                  <div className="d-flex justify-content-center mt-4">
                    <Button
                      variant="primary"
                      className={buttonClass}
                      onClick={handleSubmitRepairingOrders}
                      disabled={submitting || repairingOrderList.length === 0}
                    >
                      {submitting ? "Submitting..." : "Submit All Orders"}
                    </Button>
                  </div>
                </div>
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>
      </Modal>

      {/* Add Customer Modal */}
      <Modal
        show={showAddCustomerModal}
        onHide={handleCloseAddCustomerModal}
        centered
        size="xl"
        contentClassName={buttonTableStyle === "square" ? "rounded-0" : ""}
      >
        <Modal.Header closeButton>
          {/* <Modal.Title>Attend Customer</Modal.Title> */}
          <Modal.Title>Assign to Self</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Form onSubmit={handleAddCustomerSubmit}>
            {/* Row 1 */}
            <div className="row gy-3 mb-3">
              <div className="col-12 col-sm-6 col-lg-3">
                <Form.Label>Mobile No </Form.Label>
                <CreatableSelect
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={loadingMobileSearch}
                  options={mobileOptions}
                  value={selectedMobile}
                  inputValue={mobileInputValue}
                  {...customerSelectMenuProps}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      scheduleMobileSearch(inputValue);
                    }
                    if (action === "menu-close" || action === "set-value") {
                      clearTimeout(mobileSearchTimerRef.current);
                      setMobileInputValue("");
                      setMobileOptions([]);
                      setLoadingMobileSearch(false);
                    }
                  }}
                  onChange={handleMobileChange}
                  placeholder="Type or select Mobile No"
                  formatCreateLabel={(inputValue) => `Add new: ${inputValue}`}
                  styles={{
                    ...selectStyles,
                    control: (base, state) => ({
                      ...selectStyles.control(base, state),
                      borderColor:
                        assignMobileExistsCheck.exists &&
                          !isExistingCustomerSelected
                          ? "#ffc107"
                          : base.borderColor,
                    }),
                  }}
                />
                <MobileExistsFeedback
                  exists={
                    assignMobileExistsCheck.exists && !isExistingCustomerSelected
                  }
                  checking={assignMobileExistsCheck.checking}
                  message="Mobile already registered — you can still submit walk-in"
                  className={
                    assignMobileExistsCheck.exists && !isExistingCustomerSelected
                      ? "text-warning"
                      : ""
                  }
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <Form.Label>Full Name </Form.Label>
                <CreatableSelect
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={loadingNameSearch}
                  options={nameOptions}
                  value={selectedName}
                  inputValue={nameInputValue}
                  {...customerSelectMenuProps}
                  onInputChange={(inputValue, { action }) => {
                    if (action === "input-change") {
                      scheduleNameSearch(inputValue);
                    }
                    if (action === "menu-close" || action === "set-value") {
                      clearTimeout(nameSearchTimerRef.current);
                      setNameInputValue("");
                      setNameOptions([]);
                      setLoadingNameSearch(false);
                    }
                  }}
                  onChange={handleNameChange}
                  placeholder="Type or select Full Name"
                  formatCreateLabel={(inputValue) => `Add new: ${inputValue}`}
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <Form.Label>
                  State 
                </Form.Label>
                <Select
                  id="stateSelect"
                  options={stateOptions}
                  value={
                    selectedState
                      ? { label: state, value: selectedState }
                      : null
                  }
                  onChange={(option) => {
                    setSelectedState(option ? option.value : "");
                    setState(option ? option.label : "");
                  }}
                  styles={selectStyles}
                  placeholder="Select State"
                  classNamePrefix="react-select"
                />
              </div>
              <div className="col-12 col-sm-6 col-lg-3">
                <Form.Label>Purpose of Visit</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. looking for gold chain"
                  value={purposeOfVisit}
                  onChange={(e) => setPurposeOfVisit(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="row gy-3 mb-3">
              <div className="col-12 col-md-4">
                <Form.Label>Function Purpose</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Wedding, Social Gathering"
                  value={functionPurpose}
                  onChange={(e) => setFunctionPurpose(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>Function Date</Form.Label>
                <Form.Control
                  type="date"
                  value={functionDate}
                  onChange={(e) => setFunctionDate(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>No of Visitors</Form.Label>
                <Form.Control
                  type="number"
                  value={noOfVisitors}
                  onChange={(e) => setNoOfVisitors(e.target.value)}
                  min="1"
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </div>
            </div>

            {/* Row 3 */}
            <div className="row gy-3 mb-3">
              <div className="col-12 col-md-4">
                <Form.Label>How did you hear about us?</Form.Label>
                <div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceRelativeFriends"
                      name="reference"
                      value="Relative/Friends"
                      checked={reference === "Relative/Friends"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceRelativeFriends"
                      className="form-check-label"
                    >
                      Relative/Friends
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceSocialMedia"
                      name="reference"
                      value="Social media"
                      checked={reference === "Social media"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceSocialMedia"
                      className="form-check-label"
                    >
                      Social media
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceYoutube"
                      name="reference"
                      value="Youtube"
                      checked={reference === "Youtube"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceYoutube"
                      className="form-check-label"
                    >
                      Youtube
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceHoldings"
                      name="reference"
                      value="Holdings"
                      checked={reference === "Holdings"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceHoldings"
                      className="form-check-label"
                    >
                      Holdings
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceAutoRickshawAd"
                      name="reference"
                      value="Autorickshaw ad"
                      checked={reference === "Autorickshaw ad"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceAutoRickshawAd"
                      className="form-check-label"
                    >
                      Autorickshaw ad
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="referenceNewspaperTemplate"
                      name="reference"
                      value="Newspaper/Template"
                      checked={reference === "Newspaper/Template"}
                      onChange={(e) => setReference(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="referenceNewspaperTemplate"
                      className="form-check-label"
                    >
                      Newspaper/Template
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>Do you know about the scheme?</Form.Label>
                <div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="schemeYes"
                      name="scheme"
                      value="Yes"
                      checked={scheme === "Yes"}
                      onChange={(e) => setScheme(e.target.value)}
                      className="form-check-input"
                    />
                    <label htmlFor="schemeYes" className="form-check-label">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="schemeNo"
                      name="scheme"
                      value="No"
                      checked={scheme === "No"}
                      onChange={(e) => setScheme(e.target.value)}
                      className="form-check-input"
                    />
                    <label htmlFor="schemeNo" className="form-check-label">
                      No
                    </label>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-4">
                <Form.Label>Do you follow us on Instagram?</Form.Label>
                <div>
                  <div className="form-check mb-2">
                    <input
                      type="radio"
                      id="instagramFollowYes"
                      name="instagramFollow"
                      value="Yes"
                      checked={instagramFollow === "Yes"}
                      onChange={(e) => setInstagramFollow(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="instagramFollowYes"
                      className="form-check-label"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="radio"
                      id="instagramFollowNo"
                      name="instagramFollow"
                      value="No"
                      checked={instagramFollow === "No"}
                      onChange={(e) => setInstagramFollow(e.target.value)}
                      className="form-check-input"
                    />
                    <label
                      htmlFor="instagramFollowNo"
                      className="form-check-label"
                    >
                      No
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                type="submit"
                variant="primary"
                className={buttonClass}
                disabled={submitting}
              >
                {submitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CustomerAssignTable;
